import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoomsService } from '../../../../core/services/rooms.service';
import { RoomOccupancy, OccupancyGroupBy } from '../../../../core/models/room.model';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { PdfService } from '../../../../core/services/pdf.service';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { take, filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-occupancy-report',
    standalone: true,
    imports: [
        CommonModule, 
        FormsModule, 
        AppPrimeTable, 
        TranslateModule, 
        IconFieldModule, 
        InputIconModule, 
        InputTextModule,
        DatePickerModule,
        ButtonModule,
        TooltipModule,
        AppButton
    ],
    templateUrl: './occupancy-report.html',
})
export class OccupancyReport implements OnInit {
    occupancyData: RoomOccupancy[] = [];
    loading = false;
    isFetching = false;

    /**
     * 'room'   - one row per room, measured against clinic opening hours.
     * 'doctor' - the same rooms broken out per professional, measured against
     *            that professional's own shift hours in that room. The two
     *            differ whenever the clinic opens before the doctor's shift.
     *
     * Either way a row now covers the WHOLE filtered date range rather than a
     * single day, so a default current-month filter returns one row per room
     * instead of ~30.
     */
    groupBy: OccupancyGroupBy = 'room';

    private readonly roomCols: PrimeTableColumn[] = [
        { field: 'room_name', header: 'rooms_field_name' },
        { field: 'available_hours', header: 'AVAILABLE_HOURS', type: 'custom' },
        { field: 'scheduled_hours', header: 'SCHEDULED_HOURS', type: 'custom' },
        { field: 'booked_hours', header: 'BOOKED_HOURS', type: 'custom' },
        { field: 'slots_used', header: 'SLOTS_USED' },
        { field: 'occupancy_percentage', header: 'OCCUPANCY_VS_CLINIC', type: 'custom' },
        { field: 'scheduled_occupancy_percentage', header: 'OCCUPANCY_VS_SCHEDULE', type: 'custom' }
    ];

    private readonly doctorCols: PrimeTableColumn[] = [
        { field: 'room_name', header: 'rooms_field_name' },
        { field: 'doctor_name', header: 'schedule.professional' },
        { field: 'scheduled_hours', header: 'SCHEDULED_HOURS', type: 'custom' },
        { field: 'booked_hours', header: 'BOOKED_HOURS', type: 'custom' },
        { field: 'idle_hours', header: 'IDLE_HOURS', type: 'custom' },
        { field: 'slots_used', header: 'SLOTS_USED' },
        { field: 'occupancy_percentage', header: 'OCCUPANCY_VS_SCHEDULE', type: 'custom' }
    ];

    get cols(): PrimeTableColumn[] {
        return this.groupBy === 'doctor' ? this.doctorCols : this.roomCols;
    }

    /** True when any visible row fell back off the company's configured clinic hours. */
    get usesFallbackHours(): boolean {
        return this.occupancyData.some(r => r.hours_source && r.hours_source !== 'clinic');
    }

    // A day-level line chart of free hours was tried here and removed: with one
    // line per room the series overlap into an unreadable sawtooth, and the
    // aggregate table answers the same question more clearly. The day-level RPC
    // (get_room_occupancy_daily_v1) still exists if a different visualisation is
    // ever attempted — see supabase/migrations/room_occupancy_daily_heatmap_v4.sql.

    // Pagination
    currentPage = 1;
    pageSize = 10;
    totalPages = 0;
    totalRecords = 0;
    searchText = '';

    // Date filters
    startDate: Date | null = null;
    endDate: Date | null = null;

    /**
     * Occupancy can only be measured for days that have happened. Capping the
     * pickers at today stops a future date being picked, which would add
     * available/scheduled hours that cannot possibly have bookings yet and so
     * drag the percentage down for no real reason.
     *
     * Held as a field rather than a getter: a getter returns a new Date on every
     * change-detection pass, and p-datepicker treats the new object reference as
     * a changed input.
     */
    readonly maxDate = new Date();

    private store = inject(Store);
    private userInfo$ = this.store.select(selectUserInfo);
    private translate = inject(TranslateService);
    private pdfService = inject(PdfService);

    constructor(
        private roomsService: RoomsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        // Default to month-to-date; the table's [lazy]="true" will trigger the
        // initial load automatically.
        const { start, end } = this.getDefaultRange();
        this.startDate = start;
        this.endDate = end;
    }

    /**
     * Month-to-date: the 1st of the current month through TODAY, not the last
     * day of the month. Ending on the 30th pulled in days that have not
     * happened yet — every remaining weekday still contributes its clinic and
     * shift hours to the denominator while having no bookings, so opening the
     * report mid-month understated occupancy purely as an artefact of the
     * default filter.
     */
    private getDefaultRange(): { start: Date; end: Date } {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { start, end };
    }

    private isSameDay(a: Date | null, b: Date): boolean {
        return !!a && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    /** True once the user has moved away from the default month-to-date range or typed a search — drives the "clear filters" button. */
    get hasCustomFilters(): boolean {
        const { start, end } = this.getDefaultRange();
        return !!this.searchText || !this.isSameDay(this.startDate, start) || !this.isSameDay(this.endDate, end);
    }

    async printReport() {
        this.loading = true;
        this.cdr.detectChanges();

        try {
            const userInfo = (await firstValueFrom(
                this.userInfo$.pipe(
                    filter(u => !!u),
                    take(1)
                )
            )) as any;

            const branchId = userInfo?.branches_id || userInfo?.branch_id;
            if (!branchId) {
                console.error('No branch ID found for user');
                this.loading = false;
                this.cdr.detectChanges();
                return;
            }

            // Fetch with high limit for a complete report
            const { data } = await this.roomsService.getRoomsOccupancy(
                branchId,
                2000,
                0,
                this.startDate ? this.toDateOnlyString(this.startDate) : '',
                this.endDate ? this.toDateOnlyString(this.endDate) : '',
                this.searchText,
                this.groupBy
            );

            const allData = data?.rooms || [];

            this.getLogoBase64((logoBase64) => {
                this.pdfService.generateOccupancyReport(allData, logoBase64, this.groupBy, {
                    start: this.startDate,
                    end: this.endDate
                });
            });
        } catch (err) {
            console.error('Error fetching complete data for occupancy report:', err);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private getLogoBase64(callback: (base64: string) => void) {
        const logoPath = '/assets/images/logometasmile.png';
        fetch(logoPath)
            .then(response => response.blob())
            .then(blob => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    const base64Data = base64.split(',')[1] || base64;
                    callback(base64Data);
                };
                reader.readAsDataURL(blob);
            })
            .catch(() => {
                callback('');
            });
    }

    async onLazyLoad(event: any) {
        this.currentPage = Math.floor(event.first / event.rows) + 1;
        this.pageSize = event.rows;
        await this.loadOccupancyReport();
    }

    async applyFilters() {
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    async clearFilters() {
        this.searchText = '';
        const { start, end } = this.getDefaultRange();
        this.startDate = start;
        this.endDate = end;
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    async setGroupBy(mode: OccupancyGroupBy) {
        if (this.groupBy === mode) return;
        this.groupBy = mode;
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    async filterToday() {
        const today = new Date();
        this.startDate = today;
        this.endDate = today;
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    async loadOccupancyReport() {
        if (this.isFetching) return;

        this.isFetching = true;
        this.loading = true;
        this.cdr.detectChanges();

        const offset = (this.currentPage - 1) * this.pageSize;

        try {
            const userInfo = await firstValueFrom(
                this.userInfo$.pipe(
                    filter(u => !!u),
                    take(1)
                )
            ) as any;

            const branchId = userInfo?.branches_id || userInfo?.branch_id;
            if (!branchId) {
                console.error('No branch ID found for user');
                this.loading = false;
                this.isFetching = false;
                this.cdr.detectChanges();
                return;
            }

            const sDate = this.startDate ? this.toDateOnlyString(this.startDate) : '';
            const eDate = this.endDate ? this.toDateOnlyString(this.endDate) : '';

            const { data, error } = await this.roomsService.getRoomsOccupancy(
                branchId,
                this.pageSize,
                offset,
                sDate,
                eDate,
                this.searchText,
                this.groupBy
            );

            if (data) {
                this.occupancyData = data.rooms || [];
                this.totalPages = data.total_pages || 0;
                this.totalRecords = data.total_records || 0;
            } else {
                console.error('Error fetching occupancy report', error);
                this.occupancyData = [];
                this.totalPages = 0;
                this.totalRecords = 0;
            }        } catch (e) {
            console.error('Exception fetching occupancy report', e);
        } finally {
            this.loading = false;
            this.isFetching = false;
            this.cdr.detectChanges();
        }
    }

    async onSearch(text: string) {
        this.searchText = text;
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    async onDateChange() {
        this.currentPage = 1;
        await this.loadOccupancyReport();
    }

    formatPercentage(value: number): string {
        return value == null || isNaN(value) ? '—' : `${value.toFixed(1)}%`;
    }

    /**
     * Hours come back as a numeric with 2 decimals. A null means "not applicable"
     * (e.g. available_hours when grouped by doctor) and must not read as zero.
     */
    formatHours(value: number | null): string {
        return value == null || isNaN(value) ? '—' : `${Number(value).toFixed(1)}h`;
    }

    /** Colour band for an occupancy badge; >100% is overbooked, not "very good". */
    percentageClass(value: number | null): string {
        if (value == null || isNaN(value)) return 'bg-slate-50 text-slate-400';
        if (value > 100) return 'bg-purple-50 text-purple-700';
        if (value >= 70) return 'bg-emerald-50 text-emerald-600';
        if (value >= 40) return 'bg-amber-50 text-amber-600';
        return 'bg-rose-50 text-rose-600';
    }

    /**
     * date.toISOString() converts to UTC first, which shifts the calendar
     * day for any timezone ahead of UTC (e.g. picking the 21st locally can
     * become "20" once converted to UTC). Use the date's own local
     * year/month/day instead so what the user clicked is what gets sent.
     */
    private toDateOnlyString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
